import React, { useMemo } from "react";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { TQC2080_R01Dto } from "./Dto_TQC2080_R01";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
);

interface Props {
	data: TQC2080_R01Dto[];
}

export const Chart_TQC2080_R01: React.FC<Props> = ({ data }) => {
	const chartConfigs = useMemo(() => {
		if (!data || data.length === 0) return [];

		const grouped = data.reduce(
			(acc, item) => {
				if (!acc[item.itemNo]) acc[item.itemNo] = [];
				acc[item.itemNo].push(item);
				return acc;
			},
			{} as Record<number, TQC2080_R01Dto[]>,
		);

		return Object.entries(grouped).map(([itemNo, items]) => {
			const sorted = [...items].sort((a, b) =>
				String(a.analysisDt ?? "").localeCompare(String(b.analysisDt ?? "")),
			);
			const first = sorted[0];
			const labels = sorted.map((d) =>
				d.analysisDt && d.analysisDt.length >= 10 ? d.analysisDt.slice(5, 10) : String(d.analysisDt ?? ""),
			);
			const values = sorted.map((d) => Number(d.analysisVal ?? 0));
			const centerVal = Number(first.centerVal ?? 0);

			const chartData = {
				labels,
				datasets: [
					{
						label: "현재",
						data: values,
						borderColor: "#FB923C",
						backgroundColor: "#FB923C22",
						tension: 0.1,
						pointRadius: 3,
						spanGaps: true,
					},
					{
						label: "기준",
						data: sorted.map(() => centerVal),
						borderColor: "#3B82F6",
						borderDash: [6, 3],
						pointRadius: 0,
						tension: 0,
						fill: false,
					},
				],
			};

			const chartOptions = {
				maintainAspectRatio: false,
				responsive: true,
				plugins: {
					title: {
						display: true,
						text: `${first.testItem}`,
						font: { size: 11, weight: "bold" as const },
					},
					legend: {
						display: true,
						position: "top" as const,
						labels: { font: { size: 10 } },
					},
					tooltip: {
						mode: "index" as const,
						intersect: false,
					},
				},
				scales: {
					x: {
						ticks: {
							font: { size: 9 },
							maxRotation: 45,
						},
					},
					y: {
						ticks: { font: { size: 9 } },
					},
				},
			};

			return {
				chartData,
				chartOptions,
				itemNo: Number(itemNo),
			};
		});
	}, [data]);

	if (!data || data.length === 0) {
		return (
			<div className="flex items-center justify-center h-full text-sm text-gray-400">
				차트 데이터가 없습니다.
			</div>
		);
	}

	return (
		<div className="h-full w-full overflow-auto">
			<div className="grid grid-cols-4 gap-3 p-2">
				{chartConfigs.map((config) => (
					<div
						key={config.itemNo}
						className="bg-white border border-gray-300 rounded shadow-sm p-2"
					>
						<div style={{ height: "200px" }}>
							<Line data={config.chartData} options={config.chartOptions} />
						</div>
					</div>
				))}
			</div>
		</div>
	);
};
